import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";

const BaseURL = import.meta.env.VITE_API_URL;

export type GroupOptionManagementRecord = {
  group_option_management_id: string;
  client_id: string;
  groups: string[];
  created_at: string;
  updated_at?: string;
};

export const fetchGroupOptionManagement =
  createAsyncThunk<GroupOptionManagementRecord | null>(
    "groupOptionManagement/fetch",
    async (_, { rejectWithValue }) => {
      try {
        const res = await ipponApi.get(
          `${BaseURL}/api/v1/group-option-management`
        );
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        return list.length > 0
          ? (list[0] as GroupOptionManagementRecord)
          : null;
      } catch (error: any) {
        return rejectWithValue(
          error?.response?.data || "Failed to fetch group option management"
        );
      }
    }
  );

export const createGroupOptionManagement = createAsyncThunk<
  GroupOptionManagementRecord,
  { groups?: string[] }
>(
  "groupOptionManagement/create",
  async (body, { rejectWithValue }) => {
    try {
      const res = await ipponApi.post(
        `${BaseURL}/api/v1/group-option-management`,
        body
      );
      return res.data.data as GroupOptionManagementRecord;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || "Failed to create group option management"
      );
    }
  }
);

export const updateGroupOptionManagement = createAsyncThunk<
  GroupOptionManagementRecord,
  { group_option_management_id: string; groups?: string[] }
>(
  "groupOptionManagement/update",
  async ({ group_option_management_id, ...body }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.put(
        `${BaseURL}/api/v1/group-option-management/${group_option_management_id}`,
        body
      );
      return res.data.data as GroupOptionManagementRecord;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || "Failed to update group option management"
      );
    }
  }
);

export const upsertGroupOptionManagement = createAsyncThunk<
  GroupOptionManagementRecord,
  { existingId?: string | null; groups?: string[] }
>(
  "groupOptionManagement/upsert",
  async ({ existingId, ...body }, { dispatch, rejectWithValue }) => {
    try {
      if (existingId) {
        const resultAction: any = await dispatch(
          updateGroupOptionManagement({
            group_option_management_id: existingId,
            ...body,
          })
        );
        if (updateGroupOptionManagement.rejected.match(resultAction)) {
          throw resultAction.payload || new Error("update failed");
        }
        return resultAction.payload as GroupOptionManagementRecord;
      }
      const resultAction: any = await dispatch(
        createGroupOptionManagement(body)
      );
      if (createGroupOptionManagement.rejected.match(resultAction)) {
        throw resultAction.payload || new Error("create failed");
      }
      return resultAction.payload as GroupOptionManagementRecord;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

type GroupOptionManagementState = {
  loading: boolean;
  error: boolean;
  record: GroupOptionManagementRecord | null;
};

const initialState: GroupOptionManagementState = {
  loading: false,
  error: false,
  record: null,
};

export const groupOptionManagementSlice = createSlice({
  name: "groupOptionManagement",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchGroupOptionManagement.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(fetchGroupOptionManagement.fulfilled, (state, action) => {
        state.loading = false;
        state.record = action.payload as any;
      })
      .addCase(fetchGroupOptionManagement.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      .addCase(createGroupOptionManagement.fulfilled, (state, action) => {
        state.loading = false;
        state.record = action.payload as any;
      })
      .addCase(updateGroupOptionManagement.fulfilled, (state, action) => {
        state.loading = false;
        state.record = action.payload as any;
      })
      .addCase(upsertGroupOptionManagement.fulfilled, (state, action) => {
        state.loading = false;
        state.record = action.payload as any;
      });
  },
});

export default groupOptionManagementSlice.reducer;
